/**
 * BatchModeSelector - Template/Collection/Prompt mode selection
 */

import { FileText, Layers, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BatchMode } from '@/lib/api/batch';

interface BatchModeSelectorProps {
  mode: BatchMode;
  onModeChange: (mode: BatchMode) => void;
  disabled?: boolean;
}

const modes = [
  {
    id: 'template' as const,
    label: 'Template',
    description: 'Ein Template fur alle Bilder',
    icon: FileText,
  },
  {
    id: 'collection' as const,
    label: 'Template Collection',
    description: 'Vorkonfigurierte Template-Sammlung',
    icon: Layers,
  },
  {
    id: 'prompt' as const,
    label: 'Manuell',
    description: 'Eigener Prompt ohne Template',
    icon: MessageSquare,
  },
];

export function BatchModeSelector({
  mode,
  onModeChange,
  disabled = false,
}: BatchModeSelectorProps) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        Generierungs-Modus
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {modes.map((m) => {
          const Icon = m.icon;
          const isSelected = mode === m.id;

          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onModeChange(m.id)}
              disabled={disabled}
              className={cn(
                'flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all',
                isSelected
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20'
                  : 'border-neutral-200 dark:border-neutral-700 hover:border-orange-300 dark:hover:border-orange-700',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div
                className={cn(
                  'flex-shrink-0 p-2 rounded-lg',
                  isSelected
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500'
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div
                  className={cn(
                    'font-medium text-sm',
                    isSelected
                      ? 'text-orange-900 dark:text-orange-100'
                      : 'text-neutral-900 dark:text-neutral-100'
                  )}
                >
                  {m.label}
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  {m.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
